"use client";

import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowLeft, Home } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";

const ASCII_404 = [
  "  ██╗  ██╗  ██████╗  ██╗  ██╗",
  "  ██║  ██║ ██╔═████╗ ██║  ██║",
  "  ███████║ ██║██╔██║ ███████║",
  "  ╚════██║ ████╔╝██║ ╚════██║",
  "       ██║ ╚██████╔╝      ██║",
  "       ╚═╝  ╚═════╝       ╚═╝",
];

const GLITCH_SYMBOLS = ["▓", "░", "▒", "█", "╬", "╫", "╪", "┼"];

function useGlitch(intervalMs: number = 180) {
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setFrame((f) => f + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return frame;
}

function GlitchLine({ length, seed }: { length: number; seed: number }) {
  const chars = Array.from({ length }, (_, i) => {
    const idx = (seed + i * 7) % GLITCH_SYMBOLS.length;
    return GLITCH_SYMBOLS[idx];
  });
  return (
    <span className="text-neutral-200 select-none">
      {chars.join("")}
    </span>
  );
}

export default function NotFound() {
  const frame = useGlitch(220);

  return (
    <main className="min-h-screen w-full flex flex-col items-center justify-center bg-white px-6 py-16">
      <div className="flex flex-col items-center gap-10 max-w-xl text-center">
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-center gap-1 font-mono text-[10px] sm:text-xs leading-none"
        >
          <div className="text-neutral-300">
            <GlitchLine length={34} seed={frame} />
          </div>
          {ASCII_404.map((line, i) => (
            <motion.pre
              key={i}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              className="text-primary font-bold whitespace-pre"
            >
              {line}
            </motion.pre>
          ))}
          <div className="text-neutral-300">
            <GlitchLine length={34} seed={frame + 3} />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="flex flex-col gap-3"
        >
          <span className="inline-flex h-5 items-center rounded-lg bg-orange-100 px-1.5 font-medium text-[12px] text-orange-700 leading-4 tracking-[-0.12px] w-fit">
            Page not found
          </span>
          <h1 className="text-3xl sm:text-4xl font-medium text-neutral-900">
            This handyout took a wrong turn.
          </h1>
          <p className="text-base text-neutral-500 max-w-md mx-auto leading-relaxed">
            The page you&apos;re looking for either expired, moved, or never
            existed. Let&apos;s get you back on track.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.45 }}
          className="flex flex-row gap-3"
        >
          <Button asChild variant="default">
            <Link href="/">
              <Home className="size-4" />
              Back to homepage
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link href="/dashboard">
              <ArrowLeft className="size-4" />
              Dashboard
            </Link>
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.65 }}
          className="flex flex-col items-center gap-2 mt-8 pt-8 border-t border-neutral-100 w-full"
        >
          <span className="text-xs text-neutral-400 tracking-wider">
            Still stuck?
          </span>
          <Link
            href="/contact"
            className="text-sm text-neutral-700 hover:text-primary underline underline-offset-4 decoration-neutral-200 hover:decoration-primary transition-colors"
          >
            Get in touch with support
          </Link>
        </motion.div>
      </div>
    </main>
  );
}
